import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function AdminLoginPage() {
    const { login, admin, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (!loading && admin) return <Navigate to="/admin/videos" replace />;

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            navigate("/admin/videos", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(
                typeof detail === "string"
                    ? detail
                    : "Login failed. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <main
            data-testid="admin-login"
            className="flex min-h-screen items-center justify-center bg-brand-bg px-6 py-12 text-brand-ivory"
        >
            <div className="w-full max-w-md rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-8 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.7)]">
                <div className="mb-6 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-gold/60 bg-[#0d1117]">
                        <Lock
                            aria-hidden="true"
                            className="h-4 w-4 text-brand-gold2"
                        />
                    </span>
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            Endurance Casualty
                        </p>
                        <h1 className="font-serif text-2xl">Agency admin</h1>
                    </div>
                </div>
                <form onSubmit={onSubmit} className="space-y-4">
                    <label className="block text-sm">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-goldSoft">
                            Email
                        </span>
                        <input
                            data-testid="admin-login-email"
                            type="email"
                            autoComplete="username"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 w-full rounded-md border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-3 py-2.5 text-brand-ivory focus:border-brand-gold focus:outline-none"
                        />
                    </label>
                    <label className="block text-sm">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-goldSoft">
                            Password
                        </span>
                        <input
                            data-testid="admin-login-password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 w-full rounded-md border border-[rgba(212,175,55,0.25)] bg-[#0d1117] px-3 py-2.5 text-brand-ivory focus:border-brand-gold focus:outline-none"
                        />
                    </label>
                    {error && (
                        <p
                            data-testid="admin-login-error"
                            className="rounded-md border border-red-700/50 bg-red-900/20 px-3 py-2 text-xs text-red-100"
                        >
                            {error}
                        </p>
                    )}
                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={submitting}
                        className="enc-submit inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm disabled:opacity-60"
                    >
                        {submitting && (
                            <Loader2
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin"
                            />
                        )}
                        Sign in
                    </button>
                </form>
                <p className="mt-6 text-center text-[11px] text-brand-muted">
                    Restricted to authorized Endurance Casualty staff.
                </p>
            </div>
        </main>
    );
}
